import { Actions, ofType, createEffect } from '@ngrx/effects';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createAction, createReducer, props, on, createFeatureSelector, createSelector } from '@ngrx/store';
import { IModelEntity, IUtilsDictionary, UtilsDictionary, UtilsMisc, UtilsString } from '@pit/core';
import { IListResult, EntityDataServiceAccessorService } from '@pit/core-data';

interface IEntityEffect<T extends IModelEntity> {

    effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => Observable<Action>;

}

export interface IGenericEntityState<T extends IModelEntity> extends EntityState<T> {
    loading: boolean;
    selectedItem: T;
}

export interface IEntityReducer<T extends IModelEntity> {
    reducer: (state: IGenericEntityState<T> | undefined, action: Action) => IGenericEntityState<T>;
}

export class EntityStoreFactory<T extends IModelEntity> {

    protected _entityActions: IUtilsDictionary<any> = null;
    protected _entityEffects: IUtilsDictionary<IEntityEffect<T>> = null;
    protected _entityAdapter: EntityAdapter<T> = null;
    protected _entityReducer: any = null;
    protected _entitySelectors: IUtilsDictionary<any> = null;
    protected _featureSelector: any = null;
    protected _updateSubject: BehaviorSubject<any> = null;

    constructor(protected modelName: string, public readonly dataAccessName: string) {
        this.doCreateActions();
        this.doCreateEffects();
        this.doCreateReducer();
        this.doCreateSelectors();
    }

    public get updateSubject(): BehaviorSubject<any> {
        if (this._updateSubject === null) {
            this._updateSubject = new BehaviorSubject<any>(null);
        }

        return this._updateSubject;
    }

    public getAction(actionName: string): any {
        let result: Action = null;
        if ((UtilsString.stringIsNullOrEmpty(this.modelName) === false) && (UtilsString.stringIsNullOrEmpty(actionName) === false)) {
            result = this.entityActions.getValue(actionName);
        }

        return result;
    }

    public getEffect(effectName: string): (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => Observable<Action> {
        let result: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => Observable<Action> = null;
        if ((UtilsString.stringIsNullOrEmpty(this.modelName) === false) && (UtilsString.stringIsNullOrEmpty(effectName) === false)) {
            result = this.entityEffects.getValue(effectName)?.effect;
        }

        return result;
    }

    public getEntityReducer(): ((state: IGenericEntityState<T> | undefined, action: Action) => IGenericEntityState<T>) {
        return (state: IGenericEntityState<T> | undefined, action: Action) => this.entityReducer(state, action);
    }

    public getSelector(selectorName: string): any {
        let result: any = null;
        if ((UtilsString.stringIsNullOrEmpty(this.modelName) === false) && (UtilsString.stringIsNullOrEmpty(selectorName) === false)) {
            result = this.entitySelectors.getValue(selectorName);
        }

        return result;
    }

    protected get entityActions(): IUtilsDictionary<any> {
        if (this._entityActions === null) {
            this._entityActions = new UtilsDictionary<any>();
        }

        return this._entityActions;
    }

    protected get entityAdapter(): EntityAdapter<T> {
        if (this._entityAdapter === null) {
            this._entityAdapter = createEntityAdapter<T>({
                selectId: (entity: T) => entity.id,
                sortComparer: false
            });
        }

        return this._entityAdapter;
    }

    protected get entityEffects(): IUtilsDictionary<IEntityEffect<T>> {
        if (this._entityEffects === null) {
            this._entityEffects = new UtilsDictionary<IEntityEffect<T>>();
        }

        return this._entityEffects;
    }

    protected get entityReducer(): any {
        return this._entityReducer;
    }

    protected get entitySelectors(): IUtilsDictionary<any> {
        if (this._entitySelectors === null) {
            this._entitySelectors = new UtilsDictionary<any>();
        }

        return this._entitySelectors;
    }

    protected get featureSelector(): any {
        if (this._featureSelector === null) {
            this._featureSelector = createFeatureSelector<IGenericEntityState<T>>(this.modelName);
        }

        return this._featureSelector;
    }

    protected doCreateActions(): void {
        if (this.entityActions.isEmpty === true) {

            // Load All
            this.entityActions.add('load.all', createAction(
                UtilsString.stringFormat('[{0}}] Load All {0}s', this.modelName),
                props<{ showLoadingIndicator: boolean }>()
            ));
            this.entityActions.add('load.all.success', createAction(
                UtilsString.stringFormat('[{0}}] Load All {0}s Success', this.modelName),
                props<{ items: IListResult<T> }>()
            ));
            this.entityActions.add('load.all.failure', createAction(
                UtilsString.stringFormat('[{0}}] Load All {0}s Failure', this.modelName),
                props<{ error: any }>()
            ));

            // Load All
            this.entityActions.add('load.all.paging', createAction(
                UtilsString.stringFormat('[{0}}] Load All Paging {0}s', this.modelName),
                props<{ startRow: number, rowsToTake: number, showLoadingIndicator: boolean }>()
            ));
            this.entityActions.add('load.all.paging.success', createAction(
                UtilsString.stringFormat('[{0}}] Load All Paging {0}s Success', this.modelName),
                props<{ items: IListResult<T> }>()
            ));
            this.entityActions.add('load.all.paging.failure', createAction(
                UtilsString.stringFormat('[{0}}] Load All Paging {0}s Failure', this.modelName),
                props<{ error: any }>()
            ));

            // Load Single
            this.entityActions.add('load.single', createAction(
                UtilsString.stringFormat('[{0}}] Load Single {0}', this.modelName),
                props<{ id: number, showLoadingIndicator: boolean }>()
            ));
            this.entityActions.add('load.single.success', createAction(
                UtilsString.stringFormat('[{0}}] Load Single {0} Success', this.modelName),
                props<{ item: T }>()
            ));
            this.entityActions.add('load.single.failure', createAction(
                UtilsString.stringFormat('[{0}}] Load Single {0} Failure', this.modelName),
                props<{ error: any }>()
            ));

            // Save Single
            this.entityActions.add('save.single', createAction(
                UtilsString.stringFormat('[{0}}] Save Single {0}', this.modelName),
                props<{ item: T, showLoadingIndicator: boolean }>()
            ));
            this.entityActions.add('save.single.success', createAction(
                UtilsString.stringFormat('[{0}}] Save Single {0} Success', this.modelName),
                props<{ item: T }>()
            ));
            this.entityActions.add('save.single.failure', createAction(
                UtilsString.stringFormat('[{0}}] Save Single {0} Failure', this.modelName),
                props<{ error: any }>()
            ));

            // Delete Single
            this.entityActions.add('delete.single', createAction(
                UtilsString.stringFormat('[{0}}] Delete Single {0}', this.modelName),
                props<{ item: T, showLoadingIndicator: boolean }>()
            ));
            this.entityActions.add('delete.single.success', createAction(
                UtilsString.stringFormat('[{0}}] Delete Single {0} Success', this.modelName),
                props<{ value: any }>()
            ));
            this.entityActions.add('delete.single.failure', createAction(
                UtilsString.stringFormat('[{0}}] Delete Single {0} Failure', this.modelName),
                props<{ error: any }>()
            ));

            // Add Single
            this.entityActions.add('upsert.single', createAction(
                UtilsString.stringFormat('[{0}}] Upsert Single {0}', this.modelName),
                props<{ item: T }>()
            ));

            // Add Multi
            this.entityActions.add('upsert.multi', createAction(
                UtilsString.stringFormat('[{0}}] Upsert Multi {0}', this.modelName),
                props<{ items: T[] }>()
            ));

            // Remove Single
            this.entityActions.add('remove.single', createAction(
                UtilsString.stringFormat('[{0}}] Remove Single {0}', this.modelName),
                props<{ item: T }>()
            ));

            // Set Selected
            this.entityActions.add('set.selected', createAction(
                UtilsString.stringFormat('[{0}}] Set Selected {0}', this.modelName),
                props<{ item: T }>()
            ));

            // Set Selected By id
            this.entityActions.add('set.selected.by.id', createAction(
                UtilsString.stringFormat('[{0}}] Set Selected {0} By Id', this.modelName),
                props<{ id: number }>()
            ));

            // Set Loading
            this.entityActions.add('set.loading', createAction(
                UtilsString.stringFormat('[{0}}] Set Loading {0}', this.modelName),
                props<{ value: boolean }>()
            ));

            // Clear
            this.entityActions.add('clear', createAction(
                UtilsString.stringFormat('[{0}}] Clear All {0}', this.modelName)
            ));
        }
    }

    protected doCreateEffects(): void {
        if (this.entityEffects.isEmpty === true) {

            // Load All
            this.entityEffects.add('load.all', {
                effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                    return createEffect(() =>
                        actions$.pipe(
                            ofType(this.getAction('load.all')),
                            switchMap(action =>
                                dataServiceAccessor.getEntityDataService<T>(this.dataAccessName).getAll$().pipe(
                                    map(items => this.getAction('load.all.success')({ items: items })),
                                    catchError(error => of(this.getAction('load.all.failure')({ error: error })))
                                )
                            )
                        )
                    );
                }
            });

            this.entityEffects.add('load.all.paging', {
                effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                    return createEffect(() =>
                        actions$.pipe(
                            ofType(this.getAction('load.all.paging')),
                            map(action => action),
                            switchMap(action =>
                                dataServiceAccessor.getEntityDataService<T>(this.dataAccessName).getAllWithPaging$(action.startRow, action.rowsToTake).pipe(
                                    map(items => this.getAction('load.all.paging.success')({ items: items })),
                                    catchError(error => of(this.getAction('load.all.paging.failure')({ error: error })))
                                )
                            )
                        )
                    );
                }
            });

            // Load Single
            this.entityEffects.add('load.single', {
                effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                    return createEffect(() =>
                        actions$.pipe(
                            ofType(this.getAction('load.single')),
                            map(action => action.id),
                            switchMap(id =>
                                dataServiceAccessor.getEntityDataService<T>(this.dataAccessName).getById$(id).pipe(
                                    map(item => this.getAction('load.single.success')({ item: item })),
                                    catchError(error => of(this.getAction('load.single.failure')({ error: error })))
                                )
                            )
                        )
                    );
                }
            });

            // Save Single
            this.entityEffects.add('delete.single', {
                effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                    return createEffect(() =>
                        actions$.pipe(
                            ofType(this.getAction('delete.single')),
                            map(action => action.item),
                            switchMap(item =>
                                dataServiceAccessor.getEntityDataService<T>(this.dataAccessName).delete$(item).pipe(
                                    map(value => this.getAction('delete.single.success')({ value: value })),
                                    catchError(error => of(this.getAction('delete.single.failure')({ error: error })))
                                )
                            )
                        )
                    );
                }
            });

            // Save Single
            this.entityEffects.add('save.single', {
                effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                    return createEffect(() =>
                        actions$.pipe(
                            ofType(this.getAction('save.single')),
                            map(action => action.item),
                            switchMap(item =>
                                dataServiceAccessor.getEntityDataService<T>(this.dataAccessName).save$(item).pipe(
                                    map(item => this.getAction('save.single.success')({ item: item })),
                                    catchError(error => of(this.getAction('save.single.failure')({ error: error })))
                                )
                            )
                        )
                    );
                }
            });
        }
    }

    protected doCreateReducer() {
        if (this.entityReducer === null) {
            this._entityReducer = createReducer(
                this.entityAdapter.getInitialState({
                    loading: false,
                    selectedItem: null
                }),
                ...this.doGetReducers()
            );
        }
    }

    protected doCreateSelectors() {
        if (this.entitySelectors.isEmpty === true) {

            // Get Ids
            this.entitySelectors.add('get.ids', createSelector(
                this.featureSelector,
                this.entityAdapter.getSelectors().selectIds
            ));

            // Get Entities
            this.entitySelectors.add('get.entities', createSelector(
                this.featureSelector,
                this.entityAdapter.getSelectors().selectEntities
            ));

            // Get All Entities
            this.entitySelectors.add('get.all.entities', createSelector(
                this.featureSelector,
                this.entityAdapter.getSelectors().selectAll
            ));

            // Get Total Entities
            this.entitySelectors.add('get.total.entities', createSelector(
                this.featureSelector,
                this.entityAdapter.getSelectors().selectTotal
            ));

            // Get Entity By Id
            this.entitySelectors.add('get.entity.by.id', (id: number) => createSelector(
                this.getSelector('get.entities'),
                (entities: any[]) => entities[id]
            ));

            // Get Selected Entity Id
            this.entitySelectors.add('get.selected.entity.id', createSelector(
                this.featureSelector,
                (state: IGenericEntityState<T>) => state.selectedItem?.id
            ));

            // Get Selected Entity
            this.entitySelectors.add('get.selected.entity', createSelector(
                this.featureSelector,
                (state: IGenericEntityState<T>) => {
                    return state.selectedItem;
                }
            ));

            // Get Loading
            this.entitySelectors.add('get.loading', createSelector(
                this.featureSelector,
                (state: IGenericEntityState<T>) => state.loading
            ));
        }
    }

    protected doGetReducers<S extends IGenericEntityState<T>>(): any {
        return [
            on(
                this.getAction('delete.single'),
                this.getAction('save.single'),
                this.getAction('load.single'),
                this.getAction('load.all'),
                this.getAction('load.all.paging'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            ),
            on(
                this.getAction('delete.single.failure'),
                this.getAction('save.single.failure'),
                this.getAction('load.single.failure'),
                this.getAction('load.all.failure'),
                this.getAction('load.all.paging.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            ),
            on(
                this.getAction('delete.single.success'),
                (state: any, { value }) => {
                    let result = this.entityAdapter.removeOne(value, {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    });
                    if ((UtilsMisc.isNullOrUndefined(state.selectedItem) === false) && (state.selectedItem.id === value)) {
                        result = {
                            ...result,
                            selectedItem: null
                        };
                    }
                    return result;
                }),
            on(
                this.getAction('save.single.success'),
                (state: any, { item }) => {
                    let result = this.entityAdapter.setOne(item, {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    });
                    if ((UtilsMisc.isNullOrUndefined(state.selectedItem) === false) && (state.selectedItem.id === item.id)) {
                        result = {
                            ...result,
                            selectedItem: result.entities[item.id]
                        };
                    }
                    this.onReducer('save.single.success', item);
                    return result;
                }),
            on(this.getAction('load.all.success'), (state: any, { items }) => {
                const result = this.entityAdapter.setAll(items, {
                    ...state,
                    loading: ((state.loading === true) ? false : state.loading),
                    selectedItem: null
                });
                this.onReducer('load.all.success', items);
                return result;
            }),
            on(this.getAction('load.all.paging.success'), (state: any, { items }) => {
                const result = this.entityAdapter.setAll(items.items, {
                    ...state,
                    loading: ((state.loading === true) ? false : state.loading),
                    selectedItem: null
                });
                this.onReducer('load.all.paging.success', items.items);
                return result;
            }),
            on(this.getAction('load.single.success'), (state: any, { item }) => {
                const result = this.entityAdapter.setOne(item, {
                    ...state,
                    loading: ((state.loading === true) ? false : state.loading)
                });
                this.onReducer('load.single.success', item);
                return result;
            }),
            on(this.getAction('upsert.single'), (state: any, { item }) => {
                let result = this.entityAdapter.setOne(item, {
                    ...state
                });
                if ((UtilsMisc.isNullOrUndefined(state.selectedItem) === false) && (state.selectedItem.id === item.id)) {
                    result = {
                        ...result,
                        selectedItem: result.entities[item.id]
                    };
                }
                return result;
            }),
            on(this.getAction('upsert.multi'), (state: any, { items }) => {
                let result: any = state;
                for (let loop = 0; loop < items.length; loop++) {
                    const item: any = items[loop];
                    result = this.entityAdapter.setOne(item, {
                        ...result
                    });
                    if ((UtilsMisc.isNullOrUndefined(state.selectedItem) === false) && (state.selectedItem.id === item.id)) {
                        result = {
                            ...result,
                            selectedItem: result.entities[item.id]
                        };
                    }
                }
                return result;
            }),
            on(this.getAction('remove.single'), (state: any, { item }) => {
                let result = this.entityAdapter.removeOne(item.id, {
                    ...state
                });
                if ((UtilsMisc.isNullOrUndefined(state.selectedItem) === false) && (state.selectedItem.id === item.id)) {
                    result = {
                        ...result,
                        selectedItem: null
                    };
                }
                return result;
            }),
            on(this.getAction('set.selected'), (state: any, { item }) => {
                return {
                    ...state,
                    selectedItem: item
                };
            }),
            on(this.getAction('set.selected.by.id'), (state: any, { id }) => {
                return {
                    ...state,
                    selectedItem: state.entities[id]
                };
            }),
            on(this.getAction('set.loading'), (state: any, { value }) => {
                return {
                    ...state,
                    loading: value
                };
            }),
            on(this.getAction('clear'), (state: any) => {
                return this.entityAdapter.removeAll({
                    ...state,
                    selectedItem: null
                });
            })
        ];
    }

    protected onReducer(actionName: string, value: any): void {
    }

}
